import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, Clock, Calendar, ChevronLeft, ChevronRight, LogIn, RefreshCw, CheckCircle2, Trash2, X, Lock, LayoutGrid, CalendarDays, Sparkles, Zap, Repeat } from 'lucide-react';
import { useGoogleCalendar, TIME_BLOCK_NAMES, formatEventTime, isEventNow, getEventColor } from '../hooks/useGoogleCalendar';
import { useTasks } from '../hooks/useTasks';
import { useAuth } from '../hooks/useAuth';
import TimeBlockModal from '../components/TimeBlockModal';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import { calculateBlockCapacity, formatRemainingCapacity, getFillColor } from '../utils/timeBlockCapacity';
import { getLocalDateString } from '../utils/dateUtils';

function getInternalUserName(displayName) {
  if (!displayName) return null;
  const firstName = displayName.split(' ')[0];
  if (firstName === 'Jason') return 'Jason';
  if (firstName === 'Matt' || firstName === 'Matthew') return 'Matt';
  return firstName;
}

// Get the start date for rolling 7-day view (returns the date itself)
function getWeekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Format date for display
function formatDateShort(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Days of week starting with Sunday
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' }
];

// Work hours: 6:30 AM to 4:30 PM = 10 hours = 600 minutes
const WORK_START_HOUR = 6.5;
const WORK_END_HOUR = 16.5;
const WORK_MINUTES = (WORK_END_HOUR - WORK_START_HOUR) * 60;

// Timeline hours to display (5 AM to 9 PM) - scrollable range
const TIMELINE_START = 5;
const TIMELINE_END = 21;
const TOTAL_HOURS = TIMELINE_END - TIMELINE_START;

// Snap to 15-minute increments
const SNAP_MINUTES = 15;

// Color map for time block types - Rich colors for light mode
const BLOCK_COLORS = {
  'office time': '#C45D3A',
  'plan & organize': '#7C5CBF',
  'projects': '#3B82C4',
  'delegate and inspect': '#2D8A5F',
  'morning start': '#C44D7A',
  'eod wrap-up': '#5B6BD4'
};

function getBlockColor(name) {
  const lower = (name || '').toLowerCase();
  return BLOCK_COLORS[lower] || null;
}

// Convert hex color to very light tint for backgrounds (light mode)
function getLightBackground(hexColor) {
  // Parse hex color
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Mix with white (92% white, 8% color) for subtle tint
  const lightR = Math.round(r * 0.08 + 255 * 0.92);
  const lightG = Math.round(g * 0.08 + 255 * 0.92);
  const lightB = Math.round(b * 0.08 + 255 * 0.92);

  return `rgb(${lightR}, ${lightG}, ${lightB})`;
}

// Calculate percentage of work day filled by events (handles overlapping events)
function calculateDayFillPercentage(events) {
  if (!events || events.length === 0) return 0;

  const workStartMinutes = WORK_START_HOUR * 60;
  const workEndMinutes = WORK_END_HOUR * 60;

  // Convert events to time ranges, clamped to work hours
  const ranges = [];
  events.forEach(event => {
    if (!event.start?.dateTime) return;

    const start = new Date(event.start.dateTime);
    const end = new Date(event.end?.dateTime);

    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();

    const clampedStart = Math.max(startMinutes, workStartMinutes);
    const clampedEnd = Math.min(endMinutes, workEndMinutes);

    if (clampedEnd > clampedStart) {
      ranges.push({ start: clampedStart, end: clampedEnd });
    }
  });

  if (ranges.length === 0) return 0;

  // Sort by start time
  ranges.sort((a, b) => a.start - b.start);

  // Merge overlapping ranges
  const merged = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const current = ranges[i];
    const last = merged[merged.length - 1];

    if (current.start <= last.end) {
      // Overlapping - extend the last range if needed
      last.end = Math.max(last.end, current.end);
    } else {
      // No overlap - add new range
      merged.push(current);
    }
  }

  // Sum merged ranges
  const totalMinutes = merged.reduce((sum, range) => sum + (range.end - range.start), 0);

  return Math.min(100, Math.round((totalMinutes / WORK_MINUTES) * 100));
}

// Get position and height for timeline
function getTimelinePosition(event) {
  if (!event.start?.dateTime) return null;

  const start = new Date(event.start.dateTime);
  const end = new Date(event.end?.dateTime);

  const startHour = start.getHours() + start.getMinutes() / 60;
  const endHour = end.getHours() + end.getMinutes() / 60;

  const clampedStart = Math.max(startHour, TIMELINE_START);
  const clampedEnd = Math.min(endHour, TIMELINE_END);

  if (clampedEnd <= clampedStart) return null;

  const top = ((clampedStart - TIMELINE_START) / TOTAL_HOURS) * 100;
  const height = ((clampedEnd - clampedStart) / TOTAL_HOURS) * 100;

  return { top, height, startHour, endHour };
}

// Calculate overlapping columns for events
function calculateEventColumns(events) {
  const positioned = events
    .map(event => ({ event, pos: getTimelinePosition(event) }))
    .filter(e => e.pos !== null)
    .sort((a, b) => a.pos.startHour - b.pos.startHour);

  const columns = [];

  positioned.forEach(({ event, pos }) => {
    let placed = false;

    for (let col = 0; col < columns.length; col++) {
      const lastInCol = columns[col][columns[col].length - 1];
      if (lastInCol.pos.endHour <= pos.startHour) {
        columns[col].push({ event, pos });
        placed = true;
        break;
      }
    }

    if (!placed) {
      columns.push([{ event, pos }]);
    }
  });

  // Assign column index and total columns to each event
  const eventColumns = new Map();
  columns.forEach((col, colIndex) => {
    col.forEach(({ event }) => {
      eventColumns.set(event.id, { column: colIndex, totalColumns: columns.length });
    });
  });

  return eventColumns;
}

// Format time from hour decimal
function formatTimeFromHour(hour) {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

// Convert hour to HH:MM format
function hourToTimeString(hour) {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

export default function TimeBlocksPage({
  onAddBlock,
  onBlockSelect,
  selectedBlockId,
  getTasksForBlock: externalGetTasksForBlock
}) {
  const { user, isAdmin } = useAuth();
  const currentUserName = getInternalUserName(user?.displayName);

  const {
    isSignedIn,
    isConfigured,
    loading: gcalLoading,
    error: gcalError,
    signIn,
    signOut,
    getAllEventsForWeek,
    createTimeBlock,
    updateEvent,
    deleteEvent,
    filterTimeBlocks
  } = useGoogleCalendar();

  const { tasks } = useTasks(currentUserName);

  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [allEvents, setAllEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(0); // Index 0 = first day in rolling week (today)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // View mode: 'day' or 'week'
  const [viewMode, setViewMode] = useState('day');

  // Click-to-create prefill time
  const [prefillTime, setPrefillTime] = useState(null);

  // Focus Time AI suggestions
  const [focusSuggestions, setFocusSuggestions] = useState([]);
  const [showFocusSuggestions, setShowFocusSuggestions] = useState(false);

  // Edit modal state
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({ summary: '', startTime: '', endTime: '', description: '' });

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, event: null });

  // Current time for live indicator
  const [currentTime, setCurrentTime] = useState(new Date());

  // Drag state
  const [draggingEvent, setDraggingEvent] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const timelineRef = useRef(null);

  // Update current time every minute for live indicator
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Fetch all events when week changes or user signs in
  useEffect(() => {
    if (isSignedIn) {
      fetchEvents();
    } else {
      setAllEvents([]);
    }
  }, [isSignedIn, weekStart]);

  const fetchEvents = async () => {
    setLoading(true);
    const events = await getAllEventsForWeek(weekStart);
    setAllEvents(events || []);
    setLoading(false);
  };

  // Navigate weeks
  const goToPreviousWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() - 7);
    setWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + 7);
    setWeekStart(newStart);
  };

  const goToCurrentWeek = () => {
    setWeekStart(getWeekStart(new Date()));
    setSelectedDay(0); // First day in rolling view is always index 0
  };

  // Check if we're viewing from today
  const isCurrentWeek = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return weekStart.getTime() === today.getTime();
  }, [weekStart]);

  // Generate 7 consecutive dates starting from weekStart
  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + index);
      return date;
    });
  }, [weekStart]);

  // Group events by date string (YYYY-MM-DD) to match rolling week
  const eventsByDay = useMemo(() => {
    const grouped = {};
    // Initialize groups for each day in the rolling week
    weekDates.forEach((date, index) => {
      grouped[index] = [];
    });

    allEvents.forEach(event => {
      const eventDate = new Date(event.start?.dateTime || event.start?.date);
      const eventDateStr = getLocalDateString(eventDate);

      // Find which index in weekDates this event belongs to
      weekDates.forEach((date, index) => {
        const weekDateStr = getLocalDateString(date);
        if (eventDateStr === weekDateStr) {
          grouped[index].push(event);
        }
      });
    });

    // Sort events by start time within each day
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => {
        const aTime = new Date(a.start?.dateTime || a.start?.date);
        const bTime = new Date(b.start?.dateTime || b.start?.date);
        return aTime - bTime;
      });
    });

    return grouped;
  }, [allEvents, weekDates]);

  const getTasksForBlock = (blockId) => {
    // Use external function if provided (from SchedulePage)
    if (externalGetTasksForBlock) {
      return externalGetTasksForBlock(blockId);
    }
    return tasks.filter(t => t.gcalTimeBlockId === blockId);
  };

  const isTimeBlock = (event) => {
    const summary = event.summary?.toLowerCase() || '';
    return TIME_BLOCK_NAMES.some(name => summary.includes(name.toLowerCase()));
  };

  // Check if user can edit the event (is the organizer or event has no organizer)
  const canEditEvent = (event) => {
    if (!event) return false;
    // If no organizer info, assume we can edit (our own events)
    if (!event.organizer) return true;
    // Check if we're the organizer (self is true means we created it)
    return event.organizer.self === true;
  };

  const handleOpenAddModal = () => {
    setPrefillTime(null);
    setIsModalOpen(true);
  };

  if (onAddBlock) {
    onAddBlock.current = handleOpenAddModal;
  }

  const handleSaveBlock = async (data) => {
    const selectedDate = weekDates[selectedDay];
    const dateStr = getLocalDateString(selectedDate);
    await createTimeBlock(data.name, dateStr, data.startTime, data.endTime, data.description, data.recurrence);
    await fetchEvents();
    setPrefillTime(null);
  };

  // Click-to-create on timeline
  const handleTimelineClick = useCallback((e, dayIndex = selectedDay) => {
    if (!timelineRef.current || draggingEvent) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const percentage = Math.max(0, Math.min(1, y / rect.height));

    let clickedHour = TIMELINE_START + (percentage * (TIMELINE_END - TIMELINE_START + 1));

    // Snap to 30-minute increments
    clickedHour = Math.round(clickedHour * 2) / 2;
    clickedHour = Math.max(TIMELINE_START, Math.min(TIMELINE_END, clickedHour));

    const startHour = Math.floor(clickedHour);
    const startMin = (clickedHour % 1) * 60;
    const endHour = startHour + 1;

    const startTime = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
    const endTime = `${endHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;

    setPrefillTime({ startTime, endTime });
    if (dayIndex !== selectedDay) {
      setSelectedDay(dayIndex);
    }
    setIsModalOpen(true);
  }, [selectedDay, draggingEvent]);

  // Focus Time AI - Find gaps in schedule for deep work
  const calculateFocusSuggestions = useCallback(() => {
    const suggestions = [];
    const dayEvts = eventsByDay[selectedDay] || [];

    // Get all occupied time slots
    const occupiedSlots = dayEvts
      .filter(e => e.start?.dateTime)
      .map(e => ({
        start: new Date(e.start.dateTime).getHours() + new Date(e.start.dateTime).getMinutes() / 60,
        end: new Date(e.end.dateTime).getHours() + new Date(e.end.dateTime).getMinutes() / 60
      }))
      .sort((a, b) => a.start - b.start);

    // Find gaps of at least 1 hour during work hours
    let lastEnd = WORK_START_HOUR;

    occupiedSlots.forEach(slot => {
      if (slot.start > lastEnd) {
        const gapDuration = slot.start - lastEnd;
        if (gapDuration >= 1) {
          suggestions.push({
            startHour: lastEnd,
            endHour: Math.min(slot.start, lastEnd + 2), // Max 2 hour blocks
            duration: Math.min(gapDuration, 2),
            type: gapDuration >= 2 ? 'Deep Focus' : 'Quick Focus'
          });
        }
      }
      lastEnd = Math.max(lastEnd, slot.end);
    });

    // Check for gap at end of day
    if (lastEnd < WORK_END_HOUR) {
      const gapDuration = WORK_END_HOUR - lastEnd;
      if (gapDuration >= 1) {
        suggestions.push({
          startHour: lastEnd,
          endHour: Math.min(WORK_END_HOUR, lastEnd + 2),
          duration: Math.min(gapDuration, 2),
          type: gapDuration >= 2 ? 'Deep Focus' : 'Quick Focus'
        });
      }
    }

    setFocusSuggestions(suggestions.slice(0, 3)); // Max 3 suggestions
  }, [eventsByDay, selectedDay]);

  // Recalculate focus suggestions when events change
  useEffect(() => {
    if (isSignedIn && allEvents.length >= 0) {
      calculateFocusSuggestions();
    }
  }, [isSignedIn, allEvents, selectedDay, calculateFocusSuggestions]);

  // Add a focus time block
  const handleAddFocusBlock = async (suggestion) => {
    const selectedDate = weekDates[selectedDay];
    const dateStr = getLocalDateString(selectedDate);

    const startH = Math.floor(suggestion.startHour);
    const startM = Math.round((suggestion.startHour % 1) * 60);
    const endH = Math.floor(suggestion.endHour);
    const endM = Math.round((suggestion.endHour % 1) * 60);

    const startTime = `${startH.toString().padStart(2, '0')}:${startM.toString().padStart(2, '0')}`;
    const endTime = `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;

    await createTimeBlock('Projects', dateStr, startTime, endTime, 'Protected focus time for deep work');
    await fetchEvents();
    setShowFocusSuggestions(false);
  };

  // Edit event handlers
  const handleEventClick = (event) => {
    if (draggingEvent) return; // Don't open edit during drag

    // If onBlockSelect is provided and this is a time block, use it
    if (onBlockSelect && isTimeBlock(event)) {
      onBlockSelect(event);
      return;
    }

    // Otherwise, open edit modal (default behavior)
    const start = new Date(event.start.dateTime);
    const end = new Date(event.end.dateTime);

    setEditForm({
      summary: event.summary || '',
      startTime: `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`,
      endTime: `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`,
      description: event.description || ''
    });
    setEditingEvent(event);
  };

  const handleSaveEdit = async () => {
    if (!editingEvent) return;

    const eventDate = new Date(editingEvent.start.dateTime);
    const dateStr = getLocalDateString(eventDate);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const updatedEvent = await updateEvent(editingEvent.id, {
      summary: editForm.summary,
      description: editForm.description || undefined,
      start: {
        dateTime: `${dateStr}T${editForm.startTime}:00`,
        timeZone: timezone
      },
      end: {
        dateTime: `${dateStr}T${editForm.endTime}:00`,
        timeZone: timezone
      }
    });

    if (updatedEvent) {
      setEditingEvent(null);
      await fetchEvents();
    }
  };

  const handleDeleteEvent = async () => {
    if (!deleteConfirm.event) return;

    const success = await deleteEvent(deleteConfirm.event.id);
    if (success) {
      setDeleteConfirm({ show: false, event: null });
      setEditingEvent(null);
      await fetchEvents();
    }
  };

  // Drag handlers
  const handleDragStart = useCallback((e, event) => {
    // Don't allow dragging events we don't own
    if (!canEditEvent(event)) return;

    e.preventDefault();
    setDraggingEvent(event);

    const pos = getTimelinePosition(event);
    if (pos) {
      setDragPreview({
        startHour: pos.startHour,
        endHour: pos.endHour,
        duration: pos.endHour - pos.startHour
      });
    }
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!draggingEvent || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    const percentage = Math.max(0, Math.min(1, y / rect.height));

    let newStartHour = TIMELINE_START + (percentage * TOTAL_HOURS);

    // Snap to 15-minute increments
    const snapHours = SNAP_MINUTES / 60;
    newStartHour = Math.round(newStartHour / snapHours) * snapHours;

    // Ensure within bounds
    const duration = dragPreview?.duration || 1;
    newStartHour = Math.max(TIMELINE_START, Math.min(TIMELINE_END - duration, newStartHour));

    setDragPreview(prev => ({
      ...prev,
      startHour: newStartHour,
      endHour: newStartHour + duration
    }));
  }, [draggingEvent, dragPreview?.duration]);

  const handleDragEnd = useCallback(async () => {
    if (!draggingEvent || !dragPreview) {
      setDraggingEvent(null);
      setDragPreview(null);
      return;
    }

    const eventDate = new Date(draggingEvent.start.dateTime);
    const dateStr = getLocalDateString(eventDate);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const newStartTime = hourToTimeString(dragPreview.startHour);
    const newEndTime = hourToTimeString(dragPreview.endHour);

    setDraggingEvent(null);
    setDragPreview(null);

    await updateEvent(draggingEvent.id, {
      start: {
        dateTime: `${dateStr}T${newStartTime}:00`,
        timeZone: timezone
      },
      end: {
        dateTime: `${dateStr}T${newEndTime}:00`,
        timeZone: timezone
      }
    });

    await fetchEvents();
  }, [draggingEvent, dragPreview, updateEvent, fetchEvents]);

  // Add event listeners for drag
  useEffect(() => {
    if (draggingEvent) {
      const handleMove = (e) => handleDragMove(e);
      const handleEnd = () => handleDragEnd();

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [draggingEvent, handleDragMove, handleDragEnd]);

  const dayEvents = eventsByDay[selectedDay] || [];
  const selectedDate = weekDates[selectedDay];
  const isToday = selectedDate.toDateString() === new Date().toDateString();

  // Separate all-day events from timed events
  const allDayEvents = useMemo(() =>
    dayEvents.filter(e => e.start?.date && !e.start?.dateTime),
    [dayEvents]
  );
  const timedEvents = useMemo(() =>
    dayEvents.filter(e => e.start?.dateTime),
    [dayEvents]
  );

  // Calculate columns for overlapping events (only timed events)
  const eventColumns = useMemo(() => calculateEventColumns(timedEvents), [timedEvents]);

  // Generate time labels
  const timeLabels = [];
  for (let h = TIMELINE_START; h <= TIMELINE_END; h++) {
    const hour = h % 12 || 12;
    const ampm = h < 12 ? 'a' : 'p';
    timeLabels.push({ hour: h, label: `${hour}${ampm}` });
  }

  return (
    <div className="w-full h-full flex flex-col px-4 py-2 mobile-content-padding">
      {/* Google Calendar Connection */}
      {isConfigured && !isSignedIn && (
        <div className="mb-4 p-4 bg-surface-card rounded-xl border border-border text-center">
          <Calendar size={32} className="mx-auto text-text-muted mb-3" />
          <p className="text-text-secondary mb-3">Connect your Google Calendar to see your schedule</p>
          <button
            onClick={signIn}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-orange-hover transition-all cursor-pointer border-none"
          >
            <LogIn size={16} />
            Connect Google Calendar
          </button>
        </div>
      )}

      {!isConfigured && (
        <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <p className="text-sm text-amber-400">
            Google Calendar is not configured. Please set up the VITE_GOOGLE_CLIENT_ID environment variable.
          </p>
        </div>
      )}

      {gcalError && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <p className="text-sm text-red-400">{gcalError}</p>
        </div>
      )}

      {isSignedIn && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Compact Header Row - Day View */}
          {viewMode === 'day' && (
          <>
            {/* ========== MOBILE HEADER ========== */}
            <div className="md:hidden flex items-center justify-between gap-2 mb-3">
              {/* Day Navigator */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (selectedDay === 0) {
                      goToPreviousWeek();
                      setSelectedDay(6);
                    } else {
                      setSelectedDay(selectedDay - 1);
                    }
                  }}
                  className="p-2 rounded-lg bg-surface-card border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all cursor-pointer"
                >
                  <ChevronLeft size={18} />
                </button>

                <div className="text-center min-w-[90px]">
                  <div className={`text-sm font-semibold text-text-primary ${isToday ? 'text-accent' : ''}`}>
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'short' })} {selectedDate.getMonth() + 1}/{selectedDate.getDate()}
                    {isToday && <span className="ml-1 text-accent text-xs">Today</span>}
                  </div>
                  <div className={`text-xs font-medium ${
                    calculateDayFillPercentage(dayEvents) >= 80 ? 'text-red-400' :
                    calculateDayFillPercentage(dayEvents) >= 50 ? 'text-amber-400' :
                    'text-green-400'
                  }`}>
                    {calculateDayFillPercentage(dayEvents)}% scheduled
                  </div>
                </div>

                <button
                  onClick={() => {
                    if (selectedDay === 6) {
                      goToNextWeek();
                      setSelectedDay(0);
                    } else {
                      setSelectedDay(selectedDay + 1);
                    }
                  }}
                  className="p-2 rounded-lg bg-surface-card border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all cursor-pointer"
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5">
                {!isCurrentWeek && (
                  <button
                    onClick={goToCurrentWeek}
                    className="px-2 py-1.5 bg-accent-muted text-accent rounded-lg text-xs font-medium hover:bg-accent-muted/80 transition-all cursor-pointer border-none"
                  >
                    Today
                  </button>
                )}
                <button
                  onClick={fetchEvents}
                  disabled={loading || gcalLoading}
                  className="p-2 rounded-lg bg-surface-hover text-text-secondary hover:text-text-primary transition-all cursor-pointer border-none"
                >
                  <RefreshCw size={16} className={loading || gcalLoading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {/* ========== DESKTOP HEADER ========== */}
            <div className="hidden md:flex items-center justify-between gap-2 mb-3">
              {/* Left: Week nav + Day selector */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={goToPreviousWeek}
                  className="p-1.5 rounded-lg bg-surface-card border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all cursor-pointer"
                >
                  <ChevronLeft size={16} />
                </button>

                {/* Day Selector */}
                <div className="flex gap-1">
                  {weekDates.map((date, index) => {
                    const events = eventsByDay[index] || [];
                    const fillPercent = calculateDayFillPercentage(events);
                    const isDayToday = date.toDateString() === new Date().toDateString();
                    const dayShort = date.toLocaleDateString('en-US', { weekday: 'short' });
                    const dateShort = `${date.getMonth() + 1}/${date.getDate()}`;

                    return (
                      <button
                        key={index}
                        onClick={() => setSelectedDay(index)}
                        className={`flex-shrink-0 px-2 py-1 rounded-lg border text-sm font-medium transition-all min-w-[52px] cursor-pointer ${
                          selectedDay === index
                            ? 'border-accent bg-accent-muted text-text-primary'
                            : 'border-border bg-surface-card text-text-secondary hover:bg-surface-hover'
                        }`}
                      >
                        <div className={`text-center text-[11px] ${isDayToday ? 'font-bold' : ''}`}>
                          {dayShort} {dateShort}
                        </div>
                        <div className={`text-center text-[9px] ${
                          fillPercent >= 80 ? 'text-red-400' :
                          fillPercent >= 50 ? 'text-amber-400' :
                          'text-green-400'
                        }`}>
                          {fillPercent}%
                        </div>
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={goToNextWeek}
                  className="p-1.5 rounded-lg bg-surface-card border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all cursor-pointer"
                >
                  <ChevronRight size={16} />
                </button>

                {!isCurrentWeek && (
                  <button
                    onClick={goToCurrentWeek}
                    className="px-2 py-1 bg-accent-muted text-accent rounded text-[10px] font-medium hover:bg-accent-muted/80 transition-all cursor-pointer border-none"
                  >
                    Today
                  </button>
                )}
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-surface-card border border-border rounded-lg p-0.5">
                  <button
                    onClick={() => setViewMode('day')}
                    className={`p-1.5 rounded-md transition-all cursor-pointer border-none ${
                      viewMode === 'day'
                        ? 'bg-accent text-surface-base'
                        : 'bg-transparent text-text-secondary hover:text-text-primary'
                    }`}
                    title="Day View"
                  >
                    <CalendarDays size={14} />
                  </button>
                  <button
                    onClick={() => setViewMode('week')}
                    className={`p-1.5 rounded-md transition-all cursor-pointer border-none ${
                      viewMode === 'week'
                        ? 'bg-accent text-surface-base'
                        : 'bg-transparent text-text-secondary hover:text-text-primary'
                    }`}
                    title="Week View"
                  >
                    <LayoutGrid size={14} />
                  </button>
                </div>

                {/* Focus Time AI Button */}
                {focusSuggestions.length > 0 && (
                  <button
                    onClick={() => setShowFocusSuggestions(!showFocusSuggestions)}
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer border ${
                      showFocusSuggestions
                        ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                        : 'bg-surface-card border-border text-text-secondary hover:text-purple-400 hover:border-purple-500/50'
                    }`}
                  >
                    <Sparkles size={12} />
                    <span className="px-1 py-0.5 bg-purple-500/30 text-purple-300 text-[9px] rounded-full">
                      {focusSuggestions.length}
                    </span>
                  </button>
                )}

                <button
                  onClick={fetchEvents}
                  disabled={loading || gcalLoading}
                  className="p-1.5 rounded-lg bg-surface-hover text-text-secondary hover:text-text-primary transition-all cursor-pointer border-none"
                >
                  <RefreshCw size={14} className={loading || gcalLoading ? 'animate-spin' : ''} />
                </button>

                {isAdmin && (
                  <button
                    onClick={handleOpenAddModal}
                    className="flex items-center gap-1 px-3 py-1.5 bg-accent hover:bg-orange-hover text-white rounded-lg text-xs font-semibold shadow-sm shadow-accent/20 transition-all"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                )}
              </div>
            </div>
          </>
          )}

          {/* Compact Header Row - Week View */}
          {viewMode === 'week' && (
          <div className="flex items-center justify-between gap-2 mb-3">
            {/* Left: Week navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousWeek}
                className="p-1.5 rounded-lg bg-surface-card border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all cursor-pointer"
              >
                <ChevronLeft size={16} />
              </button>

              <span className="text-xs font-medium text-text-primary whitespace-nowrap">
                {formatDateShort(weekStart)} - {formatDateShort(new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000))}
              </span>

              <button
                onClick={goToNextWeek}
                className="p-1.5 rounded-lg bg-surface-card border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all cursor-pointer"
              >
                <ChevronRight size={16} />
              </button>

              {!isCurrentWeek && (
                <button
                  onClick={goToCurrentWeek}
                  className="px-2 py-1 bg-accent-muted text-accent rounded text-[10px] font-medium hover:bg-accent-muted/80 transition-all cursor-pointer border-none"
                >
                  Today
                </button>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* View Mode Toggle */}
              <div className="hidden md:flex items-center bg-surface-card border border-border rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('day')}
                  className={`p-1.5 rounded-md transition-all cursor-pointer border-none ${
                    viewMode === 'day'
                      ? 'bg-accent text-surface-base'
                      : 'bg-transparent text-text-secondary hover:text-text-primary'
                  }`}
                  title="Day View"
                >
                  <CalendarDays size={14} />
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`p-1.5 rounded-md transition-all cursor-pointer border-none ${
                    viewMode === 'week'
                      ? 'bg-accent text-surface-base'
                      : 'bg-transparent text-text-secondary hover:text-text-primary'
                  }`}
                  title="Week View"
                >
                  <LayoutGrid size={14} />
                </button>
              </div>

              <button
                onClick={fetchEvents}
                disabled={loading || gcalLoading}
                className="p-1.5 rounded-lg bg-surface-hover text-text-secondary hover:text-text-primary transition-all cursor-pointer border-none"
              >
                <RefreshCw size={14} className={loading || gcalLoading ? 'animate-spin' : ''} />
              </button>

              {isAdmin && (
                <button
                  onClick={handleOpenAddModal}
                  className="hidden md:flex items-center gap-1 px-3 py-1.5 bg-accent hover:bg-orange-hover text-white rounded-lg text-xs font-semibold shadow-sm shadow-accent/20 transition-all"
                >
                  <Plus size={14} />
                  Add
                </button>
              )}
            </div>
          </div>
          )}

          {/* Focus Time AI Suggestions Panel */}
          {showFocusSuggestions && focusSuggestions.length > 0 && (
            <div className="mb-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-purple-400" />
                <span className="text-sm font-medium text-purple-300">Focus Time Suggestions</span>
                <span className="text-xs text-purple-400/70">AI-detected free slots for deep work</span>
              </div>
              <div className="space-y-2">
                {focusSuggestions.map((suggestion, idx) => {
                  const startH = Math.floor(suggestion.startHour);
                  const startM = Math.round((suggestion.startHour % 1) * 60);
                  const endH = Math.floor(suggestion.endHour);
                  const endM = Math.round((suggestion.endHour % 1) * 60);
                  const startAmPm = startH >= 12 ? 'PM' : 'AM';
                  const endAmPm = endH >= 12 ? 'PM' : 'AM';
                  const startH12 = startH % 12 || 12;
                  const endH12 = endH % 12 || 12;

                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-surface-card rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${suggestion.type === 'Deep Focus' ? 'bg-blue-500/20' : 'bg-green-500/20'}`}>
                          <Zap size={14} className={suggestion.type === 'Deep Focus' ? 'text-blue-400' : 'text-green-400'} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-text-primary">{suggestion.type}</div>
                          <div className="text-xs text-text-tertiary">
                            {startH12}:{startM.toString().padStart(2, '0')} {startAmPm} - {endH12}:{endM.toString().padStart(2, '0')} {endAmPm}
                            <span className="ml-2 text-purple-400">({suggestion.duration}h available)</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddFocusBlock(suggestion)}
                        className="px-3 py-1.5 bg-purple-500/20 text-purple-300 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-all cursor-pointer border-none"
                      >
                        + Block Time
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All-Day Events Section */}
          {allDayEvents.length > 0 && (
            <div className="mb-4 p-3 bg-surface-card border border-border rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={14} className="text-text-tertiary" />
                <span className="text-xs font-medium text-text-secondary">All-Day Events</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {allDayEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    className="px-3 py-1.5 bg-accent/20 border border-accent/30 rounded-lg text-xs font-medium text-text-primary cursor-pointer hover:bg-accent/30 transition-all"
                  >
                    {event.summary || 'Untitled'}
                    {event.recurrence && <Repeat size={10} className="inline ml-1 text-text-tertiary" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline View - Day or Week */}
          <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : viewMode === 'week' ? (
            /* Week View */
            <div className="bg-surface-card rounded-xl border border-border overflow-hidden overflow-x-auto">
              <div className="flex min-w-[800px]">
                {/* Time labels column */}
                <div className="w-12 flex-shrink-0 border-r border-border">
                  <div className="h-10 border-b border-border" /> {/* Header spacer */}
                  {timeLabels.map(({ hour, label }) => (
                    <div
                      key={hour}
                      className="h-12 flex items-start justify-end pr-2 pt-0 text-[10px] text-text-tertiary"
                    >
                      {label}
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {weekDates.map((date, dayIndex) => {
                  const dayEvts = eventsByDay[dayIndex] || [];
                  const timedEvts = dayEvts.filter(e => e.start?.dateTime);
                  const dayIsToday = date.toDateString() === new Date().toDateString();
                  const dayColumns = calculateEventColumns(timedEvts);
                  const dayShort = date.toLocaleDateString('en-US', { weekday: 'short' });

                  return (
                    <div key={dayIndex} className="flex-1 min-w-[100px] border-r border-border last:border-r-0">
                      {/* Day header */}
                      <div
                        className={`h-10 flex flex-col items-center justify-center border-b border-border cursor-pointer hover:bg-surface-hover transition-all ${
                          dayIsToday ? 'bg-accent-muted' : ''
                        }`}
                        onClick={() => {
                          setSelectedDay(dayIndex);
                          setViewMode('day');
                        }}
                      >
                        <span className={`text-[10px] ${dayIsToday ? 'text-accent font-medium' : 'text-text-tertiary'}`}>
                          {dayShort}
                        </span>
                        <span className={`text-xs ${dayIsToday ? 'text-accent font-bold' : 'text-text-primary'}`}>
                          {date.getDate()}
                        </span>
                      </div>

                      {/* Events timeline */}
                      <div
                        className="relative cursor-pointer"
                        style={{ height: `${(TIMELINE_END - TIMELINE_START + 1) * 48}px` }}
                        onClick={(e) => {
                          if (e.target === e.currentTarget) {
                            handleTimelineClick(e, dayIndex);
                          }
                        }}
                      >
                        {/* Hour lines */}
                        {timeLabels.map(({ hour }) => (
                          <div
                            key={hour}
                            className="absolute left-0 right-0 border-t border-border/50"
                            style={{ top: `${((hour - TIMELINE_START) / (TIMELINE_END - TIMELINE_START + 1)) * 100}%` }}
                          />
                        ))}

                        {/* Current time indicator */}
                        {dayIsToday && (() => {
                          const ch = currentTime.getHours() + currentTime.getMinutes() / 60;
                          if (ch >= TIMELINE_START && ch <= TIMELINE_END) {
                            const top = ((ch - TIMELINE_START) / (TIMELINE_END - TIMELINE_START + 1)) * 100;
                            return (
                              <div
                                className="absolute left-0 right-0 border-t-2 border-red-500 z-20 pointer-events-none"
                                style={{ top: `${top}%` }}
                              />
                            );
                          }
                          return null;
                        })()}

                        {/* Events */}
                        {timedEvts.map((event) => {
                          const pos = getTimelinePosition(event);
                          if (!pos) return null;

                          const colInfo = dayColumns.get(event.id) || { column: 0, totalColumns: 1 };
                          const colWidth = 100 / colInfo.totalColumns;
                          const leftOff = colInfo.column * colWidth;
                          const blockColor = getBlockColor(event.summary) || getEventColor(event);

                          return (
                            <div
                              key={event.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEventClick(event);
                              }}
                              className="absolute rounded overflow-hidden cursor-pointer shadow-sm hover:shadow-md hover:ring-1 hover:ring-accent/50 transition-shadow"
                              style={{
                                top: `calc(${pos.top}% + 2px)`,
                                height: `calc(${Math.max(pos.height, 1.5)}% - 4px)`,
                                minHeight: '24px',
                                left: `calc(${leftOff}% + 4px)`,
                                width: `calc(${colWidth}% - 8px)`,
                                backgroundColor: getLightBackground(blockColor),
                                borderLeft: `3px solid ${blockColor}`
                              }}
                            >
                              <div className="p-1 h-full overflow-hidden flex items-center">
                                <span className="text-[10px] font-semibold text-gray-800 line-clamp-2">
                                  {event.summary || 'Untitled'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Day View */
            <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
              <div className="flex">
                {/* Time labels column */}
                <div className="w-12 flex-shrink-0 border-r border-border">
                  {timeLabels.map(({ hour, label }) => (
                    <div
                      key={hour}
                      className="flex items-start justify-end pr-2 pt-0 text-[11px] text-text-tertiary"
                      style={{ height: '60px' }}
                    >
                      {label}
                    </div>
                  ))}
                </div>

                {/* Events column - Click to create */}
                <div
                  ref={timelineRef}
                  onClick={(e) => {
                    // Only trigger if clicking on empty space (not on events)
                    if (e.target === e.currentTarget || e.target.classList.contains('timeline-bg')) {
                      handleTimelineClick(e);
                    }
                  }}
                  className={`flex-1 relative ${draggingEvent ? 'cursor-grabbing' : 'cursor-pointer'}`}
                  style={{ height: `${(TIMELINE_END - TIMELINE_START + 1) * 60}px` }}
                  title="Click to add event"
                >
                  {/* Hour lines */}
                  {timeLabels.map(({ hour }) => (
                    <div
                      key={hour}
                      className="absolute left-0 right-0 border-t border-border"
                      style={{ top: `${((hour - TIMELINE_START) / (TIMELINE_END - TIMELINE_START + 1)) * 100}%` }}
                    />
                  ))}

                  {/* Work hours indicator - subtle vertical lines on edges */}
                  <div
                    className="absolute left-0 w-0.5 bg-green-400/30 pointer-events-none"
                    style={{
                      top: `${((WORK_START_HOUR - TIMELINE_START) / (TIMELINE_END - TIMELINE_START + 1)) * 100}%`,
                      height: `${((WORK_END_HOUR - WORK_START_HOUR) / (TIMELINE_END - TIMELINE_START + 1)) * 100}%`
                    }}
                  />

                  {/* Current time indicator (red, updates live) */}
                  {isToday && (() => {
                    const currentHour = currentTime.getHours() + currentTime.getMinutes() / 60;
                    if (currentHour >= TIMELINE_START && currentHour <= TIMELINE_END) {
                      const top = ((currentHour - TIMELINE_START) / (TIMELINE_END - TIMELINE_START + 1)) * 100;
                      return (
                        <div
                          className="absolute left-0 right-0 border-t-2 border-red-500 z-20 pointer-events-none"
                          style={{ top: `${top}%` }}
                        >
                          <div className="absolute -left-1 -top-1.5 w-3 h-3 rounded-full bg-red-500" />
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Drag preview */}
                  {draggingEvent && dragPreview && (
                    <div
                      className="absolute left-1 right-1 rounded-lg border-2 border-dashed border-accent bg-accent/20 z-30 pointer-events-none"
                      style={{
                        top: `${((dragPreview.startHour - TIMELINE_START) / TOTAL_HOURS) * 100}%`,
                        height: `${((dragPreview.endHour - dragPreview.startHour) / TOTAL_HOURS) * 100}%`
                      }}
                    >
                      <div className="p-2 text-xs font-medium text-accent">
                        {formatTimeFromHour(dragPreview.startHour)} - {formatTimeFromHour(dragPreview.endHour)}
                      </div>
                    </div>
                  )}

                  {/* Events (timed events only - all-day shown above) */}
                  {timedEvents.map((event) => {
                    const pos = getTimelinePosition(event);
                    if (!pos) return null;

                    const columnInfo = eventColumns.get(event.id) || { column: 0, totalColumns: 1 };
                    const columnWidth = 100 / columnInfo.totalColumns;
                    const leftOffset = columnInfo.column * columnWidth;

                    const isBlock = isTimeBlock(event);
                    const blockColor = getBlockColor(event.summary);
                    const eventColor = blockColor || getEventColor(event);
                    const blockTasks = isBlock ? getTasksForBlock(event.id) : [];
                    const capacity = isBlock ? calculateBlockCapacity(event, blockTasks) : null;
                    const isNow = isEventNow(event);
                    const isDragging = draggingEvent?.id === event.id;
                    const isEditable = canEditEvent(event);
                    const isSelected = selectedBlockId === event.id;

                    // Calculate if this is a short event (under 30 min)
                    const durationMinutes = (pos.endHour - pos.startHour) * 60;
                    const isShortEvent = durationMinutes <= 30;

                    return (
                      <div
                        key={event.id}
                        onClick={() => handleEventClick(event)}
                        onMouseDown={(e) => isEditable && handleDragStart(e, event)}
                        onTouchStart={(e) => isEditable && handleDragStart(e, event)}
                        className={`group absolute rounded-md overflow-visible select-none shadow-sm transition-all duration-200 hover:shadow-lg hover:!z-[500] ${
                          isNow ? 'ring-2 ring-accent shadow-lg' : ''
                        } ${isSelected ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface-base shadow-lg !z-[400]' : ''} ${isDragging ? 'opacity-50' : ''} ${
                          isEditable ? 'cursor-grab' : 'cursor-pointer'
                        }`}
                        style={{
                          top: `calc(${pos.top}% + 1px)`,
                          height: `calc(${Math.max(pos.height, 1.5)}% - 2px)`,
                          minHeight: isShortEvent ? '22px' : '28px',
                          left: `calc(${leftOffset}% + 8px)`,
                          width: `calc(${columnWidth}% - 16px)`,
                          backgroundColor: getLightBackground(eventColor),
                          borderLeft: `3px solid ${eventColor}`,
                          zIndex: isNow ? 30 : 10 + columnInfo.column
                        }}
                      >
                        {/* Capacity fill indicator for time blocks */}
                        {isBlock && capacity && (
                          <div
                            className="absolute inset-0 rounded-md pointer-events-none"
                            style={{
                              background: `linear-gradient(to right, ${getFillColor(capacity.fillPercentage, capacity.isOverbooked)}25 ${Math.min(capacity.fillPercentage, 100)}%, transparent ${Math.min(capacity.fillPercentage, 100)}%)`,
                              zIndex: 0
                            }}
                          />
                        )}

                        {/* Compact default view */}
                        <div className={`relative px-2 py-1 h-full overflow-hidden flex items-center ${isShortEvent ? 'gap-1' : 'flex-col justify-center items-start gap-0'}`}>
                          <span className={`font-semibold text-gray-800 truncate w-full ${isShortEvent ? 'text-[10px]' : 'text-[11px]'}`}>
                            {event.summary || 'Untitled'}
                          </span>
                          {!isShortEvent && (
                            <span className="text-[9px] text-gray-500 truncate w-full">
                              {formatEventTime(event)}
                            </span>
                          )}
                        </div>

                        {/* Expanded hover tooltip */}
                        <div className="absolute left-0 top-full mt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100] pointer-events-none">
                          <div
                            className="bg-white rounded-lg shadow-xl border border-border p-3 min-w-[200px] max-w-[280px]"
                            style={{ borderLeft: `4px solid ${eventColor}` }}
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-sm font-semibold text-gray-800">
                                    {event.summary || 'Untitled'}
                                  </span>
                                  {isBlock && (
                                    <span
                                      className="text-[9px] px-1.5 py-0.5 rounded font-medium"
                                      style={{ backgroundColor: eventColor, color: '#fff' }}
                                    >
                                      Block
                                    </span>
                                  )}
                                  {!isEditable && (
                                    <Lock size={10} className="text-gray-400" />
                                  )}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {formatEventTime(event)}
                                </div>
                                {event.description && (
                                  <div className="text-[11px] text-gray-500 mt-2 line-clamp-2">
                                    {event.description}
                                  </div>
                                )}
                              </div>
                            </div>

                            {blockTasks.length > 0 && capacity && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                {/* Capacity summary */}
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-[10px] text-gray-500">
                                    {capacity.scheduledMinutes} / {capacity.totalMinutes} min
                                  </span>
                                  <span className={`text-[10px] font-medium ${
                                    capacity.isOverbooked ? 'text-red-500' : 'text-green-600'
                                  }`}>
                                    {formatRemainingCapacity(capacity.remainingMinutes)}
                                  </span>
                                </div>
                                {/* Scrollable task list - shows ALL tasks */}
                                <div className="max-h-32 overflow-y-auto space-y-1 pr-1">
                                  {blockTasks.map(task => (
                                    <div key={task.id} className="flex items-center gap-1.5 text-[11px]">
                                      <CheckCircle2
                                        size={12}
                                        className={task.completedAt ? 'text-green-600' : 'text-gray-300'}
                                      />
                                      <span className={`flex-1 truncate ${task.completedAt ? 'line-through text-gray-400' : 'text-gray-600'}`}>
                                        {task.title}
                                      </span>
                                      <span className="text-[9px] text-gray-400 flex-shrink-0">
                                        {task.timeEstimate || '15'}m
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Empty state */}
                  {timedEvents.length === 0 && allDayEvents.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center timeline-bg">
                      <div className="text-center pointer-events-none">
                        <Clock size={24} className="mx-auto text-text-muted mb-2" />
                        <p className="text-sm text-text-tertiary">No events this day</p>
                        <p className="text-xs text-text-muted mt-1">Click anywhere to add an event</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Add Block Modal */}
      <TimeBlockModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPrefillTime(null);
        }}
        onSave={handleSaveBlock}
        selectedDate={selectedDate}
        prefillTime={prefillTime}
      />

      {/* Edit Event Modal */}
      <Modal
        isOpen={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        title={canEditEvent(editingEvent) ? "Edit Event" : "Event Details"}
      >
        <div className="flex flex-col gap-4">
          {/* Read-only notice for non-organizer events */}
          {!canEditEvent(editingEvent) && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-400">
                You can't edit this event because you're not the organizer.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Event Name
            </label>
            <input
              type="text"
              value={editForm.summary}
              onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}
              disabled={!canEditEvent(editingEvent)}
              className={`w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none transition-all ${
                canEditEvent(editingEvent)
                  ? 'focus:border-accent focus:ring-2 focus:ring-accent-muted'
                  : 'opacity-60 cursor-not-allowed'
              }`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-2 text-text-secondary">
                <Clock size={14} className="inline mr-1.5" />
                Start Time
              </label>
              <input
                type="time"
                value={editForm.startTime}
                onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                disabled={!canEditEvent(editingEvent)}
                className={`w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none transition-all ${
                  canEditEvent(editingEvent)
                    ? 'focus:border-accent focus:ring-2 focus:ring-accent-muted'
                    : 'opacity-60 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-text-secondary">
                End Time
              </label>
              <input
                type="time"
                value={editForm.endTime}
                onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                disabled={!canEditEvent(editingEvent)}
                className={`w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none transition-all ${
                  canEditEvent(editingEvent)
                    ? 'focus:border-accent focus:ring-2 focus:ring-accent-muted'
                    : 'opacity-60 cursor-not-allowed'
                }`}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Description
            </label>
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Add notes, agenda, or details..."
              rows={3}
              disabled={!canEditEvent(editingEvent)}
              className={`w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none transition-all resize-none ${
                canEditEvent(editingEvent)
                  ? 'focus:border-accent focus:ring-2 focus:ring-accent-muted'
                  : 'opacity-60 cursor-not-allowed'
              }`}
            />
          </div>

          {/* Recurrence indicator */}
          {editingEvent?.recurrence && (
            <div className="flex items-center gap-2 p-3 bg-surface-hover rounded-lg">
              <Repeat size={14} className="text-text-tertiary" />
              <span className="text-xs text-text-secondary">This is a recurring event</span>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            {canEditEvent(editingEvent) && (
              <button
                type="button"
                onClick={() => setDeleteConfirm({ show: true, event: editingEvent })}
                className="py-3 px-4 bg-red-500/10 text-red-400 border border-red-500/30 rounded-xl text-sm font-medium cursor-pointer hover:bg-red-500/20 transition-all flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditingEvent(null)}
              className="flex-1 py-3 px-4 bg-surface-hover text-text-secondary border border-border rounded-xl text-sm font-medium cursor-pointer hover:bg-surface-active hover:text-text-primary transition-all"
            >
              {canEditEvent(editingEvent) ? 'Cancel' : 'Close'}
            </button>
            {canEditEvent(editingEvent) && (
              <button
                type="button"
                onClick={handleSaveEdit}
                className="flex-1 py-3 px-4 bg-accent hover:bg-orange-hover text-white rounded-xl text-sm font-semibold cursor-pointer shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Save Changes
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, event: null })}
        onConfirm={handleDeleteEvent}
        title="Delete Event"
        message={`Are you sure you want to delete "${deleteConfirm.event?.summary}"? This will remove it from your Google Calendar.`}
      />
    </div>
  );
}
